import type {
	Area,
	DiningTable,
	LiveBoard,
	LiveOrder,
	ServiceRequestType,
	TablesList,
} from "@luckymenu/core";
import { tablesByArea } from "../tables/tables";

/**
 * The Tables tab (OP-06, ux-design.md, Tables): every table in use, grouped
 * by area, with its state, amount and time seated. Pure functions over
 * `staff.tables.list` and `staff.ordering.listLive`.
 */

export type TableState =
	/** No open order. */
	| "free"
	/** An open order. */
	| "occupied"
	/** The diners asked for the bill (an unresolved `bill` request). */
	| "billRequested"
	/** A bill is printed and waiting for payment (Phase 4). */
	| "billed";

export type TableTile = {
	table: DiningTable;
	state: TableState;
	order: LiveOrder | null;
	/** The order's total, or 0. */
	amountPaise: number;
	/** When the order opened, or null. */
	seatedAt: string | null;
	/** A QR batch from this table waits for a decision. */
	newOrder: boolean;
	/** Other calls not resolved yet (waiter, water, other), one of each. */
	calls: ServiceRequestType[];
};

export type FloorArea = { area: Area | null; tiles: TableTile[] };

export function tableTile(table: DiningTable, board: LiveBoard): TableTile {
	const order = board.orders.find((o) => o.tableId === table.id) ?? null;
	const requests = board.serviceRequests.filter(
		(r) => r.tableId === table.id && r.status !== "resolved",
	);
	const billRequested = requests.some((r) => r.type === "bill");
	const state: TableState =
		order?.status === "billed"
			? "billed"
			: billRequested
				? "billRequested"
				: order
					? "occupied"
					: "free";
	return {
		table,
		state,
		order,
		amountPaise: order?.totalPaise ?? 0,
		seatedAt: order?.openedAt ?? null,
		newOrder: board.pendingBatches.some((b) => b.tableId === table.id && b.status === "pending"),
		calls: [...new Set(requests.filter((r) => r.type !== "bill").map((r) => r.type))],
	};
}

/** Areas in their order, then tables without an area; tables not in use are left out. */
export function floorPlan(tables: TablesList, board: LiveBoard): FloorArea[] {
	return tablesByArea({ ...tables, tables: tables.tables.filter((t) => t.isActive) })
		.map((group) => ({
			area: group.area,
			tiles: group.tables.map((t) => tableTile(t, board)),
		}))
		.filter((group) => group.tiles.length > 0);
}

export type FloorCounts = Record<TableState, number>;

export function floorCounts(areas: readonly FloorArea[]): FloorCounts {
	const counts: FloorCounts = { free: 0, occupied: 0, billRequested: 0, billed: 0 };
	for (const a of areas) for (const t of a.tiles) counts[t.state]++;
	return counts;
}
